import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_FORMATS_LABEL,
  MAX_ATTACHMENT_SIZE_MB,
  prepareAttachmentsForUpload,
  type AttachmentError,
} from '../utils/attachmentFiles';

/** A file the picker could not take: the shared reasons plus too many files. */
type PickerError = { name: string; reason: AttachmentError['reason'] | 'tooMany' };

/** Most files one message can carry (the API's limit). */
export const MAX_MESSAGE_FILES = 10;

interface MessageAttachmentPickerProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}

/** Touch devices (phone, tablet) get the camera; a computer only the paperclip (ClickUp 869fmfpnn). */
const TOUCH_QUERY = '(pointer: coarse)';
const useTouchDevice = () => {
  const [touch, setTouch] = useState(() => window.matchMedia?.(TOUCH_QUERY)?.matches ?? false);
  useEffect(() => {
    const query = window.matchMedia?.(TOUCH_QUERY);
    if (!query) return;
    const update = () => setTouch(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return touch;
};

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Attach files to a message (ClickUp 869fj6n1d): a paperclip for any supported
 * file and, on a touch device only, a camera that opens the phone's camera
 * directly (ClickUp 869fmfpnn); on a computer it would just repeat the file
 * picker. The chosen files show
 * as chips that can be removed before sending; files that cannot be sent are
 * named with the reason. The same rules as the document library: the
 * supported types and at most 25 MB per file.
 */
export function MessageAttachmentPicker({ files, onChange, disabled = false }: MessageAttachmentPickerProps) {
  const { t } = useTranslation();
  const id = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<PickerError[]>([]);
  const [preparing, setPreparing] = useState(false);
  const touchDevice = useTouchDevice();

  const add = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (chosen.length === 0) return;
    setPreparing(true);
    try {
      const prepared = await prepareAttachmentsForUpload(chosen);
      const room = Math.max(0, MAX_MESSAGE_FILES - files.length);
      const tooMany: PickerError[] = prepared.files.slice(room).map(file => ({ name: file.name, reason: 'tooMany' }));
      setErrors([...prepared.errors, ...tooMany]);
      onChange([...files, ...prepared.files.slice(0, room)]);
    } finally {
      setPreparing(false);
    }
  };

  const errorText = (error: PickerError) => {
    const reason = error.reason;
    if (reason === 'tooMany') return t('messageAttachments.errors.tooMany', { name: error.name, max: MAX_MESSAGE_FILES });
    return `${error.name}: ${t(`contracts.attachments.errors.${reason}`, { formats: ATTACHMENT_FORMATS_LABEL, max: MAX_ATTACHMENT_SIZE_MB })}`;
  };

  return (
    <div className="message-attachment-picker">
      <div className="message-attachment-buttons">
        <input ref={fileInput} id={`${id}-file`} type="file" multiple accept={ATTACHMENT_ACCEPT} className="visually-hidden"
          tabIndex={-1} onChange={event => void add(event)} data-testid="message-attachment-input" />
        {touchDevice && <input ref={cameraInput} id={`${id}-camera`} type="file" accept="image/*" capture="environment" className="visually-hidden"
          tabIndex={-1} onChange={event => void add(event)} data-testid="message-attachment-camera" />}
        <button type="button" className="message-attachment-button" disabled={disabled || preparing || files.length >= MAX_MESSAGE_FILES}
          onClick={() => fileInput.current?.click()} aria-label={t('messageAttachments.attach')} title={t('messageAttachments.attach')}>
          <i className="feather-paperclip" aria-hidden="true"></i>
        </button>
        {touchDevice && <button type="button" className="message-attachment-button" disabled={disabled || preparing || files.length >= MAX_MESSAGE_FILES}
          onClick={() => cameraInput.current?.click()} aria-label={t('messageAttachments.photo')} title={t('messageAttachments.photo')}
          data-testid="message-attachment-camera-button">
          <i className="feather-camera" aria-hidden="true"></i>
        </button>}
        {preparing && <small className="text-muted" role="status">{t('messageAttachments.preparing')}</small>}
      </div>
      {files.length > 0 && (
        <ul className="message-attachment-chips" aria-label={t('messageAttachments.chosen')}>
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className="message-attachment-chip">
              <i className={`feather-${file.type.startsWith('image/') ? 'image' : 'file-text'}`} aria-hidden="true"></i>
              <span className="message-attachment-chip-name">{file.name}</span>
              <small>{formatSize(file.size)}</small>
              <button type="button" className="message-attachment-chip-remove" disabled={disabled}
                aria-label={t('messageAttachments.remove', { name: file.name })}
                onClick={() => onChange(files.filter((_, other) => other !== index))}>
                <i className="feather-x" aria-hidden="true"></i>
              </button>
            </li>
          ))}
        </ul>
      )}
      {errors.length > 0 && (
        <ul className="message-attachment-errors" role="alert">
          {errors.map((error, index) => <li key={`${error.name}-${index}`}>{errorText(error)}</li>)}
        </ul>
      )}
    </div>
  );
}
